import { useRef, useState, useMemo, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {
  OrbitControls,
  Html,
  Trail,
  Sphere
} from '@react-three/drei';
import * as THREE from 'three';
import type { DatabaseState, Mushroom, Compound, Condition, Study } from '@/types/database';

interface KnowledgeGraph3DProps {
  database: DatabaseState;
  autoRotate?: boolean;
}

interface MushroomNodeProps {
  mushroom: Mushroom;
  position: [number, number, number];
  onClick: () => void;
  onHover: (hover: boolean) => void;
}

interface CompoundNodeProps {
  compound: Compound;
  position: [number, number, number];
  onClick: () => void;
  onHover: (hover: boolean) => void;
}

interface ConditionNodeProps {
  condition: Condition;
  position: [number, number, number];
  onClick: () => void;
  onHover: (hover: boolean) => void;
}

interface StudyParticleProps {
  study: Study;
  start: [number, number, number];
  end: [number, number, number];
  speed: number;
}

// Mushroom Node Component (Orange Spheres)
function MushroomNode({ mushroom, position, onClick, onHover }: MushroomNodeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.001;
      if (hovered) {
        meshRef.current.scale.lerp(new THREE.Vector3(1.2, 1.2, 1.2), 0.1);
      } else {
        meshRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
      }
    }
  });

  const size = Math.max(1.5, Math.min(3, mushroom.total_studies / 10));

  return (
    <group position={position}>
      <Sphere
        ref={meshRef}
        args={[size, 32, 32]}
        onClick={onClick}
        onPointerOver={() => {
          setHovered(true);
          onHover(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          onHover(false);
          document.body.style.cursor = 'auto';
        }}
      >
        <meshStandardMaterial
          color="#C8A53D"
          emissive="#C8A53D"
          emissiveIntensity={hovered ? 0.3 : 0.1}
          roughness={0.4}
          metalness={0.6}
        />
      </Sphere>
      {hovered && (
        <Html distanceFactor={10}>
          <div className="bg-black/80 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap">
            <div className="font-bold">{mushroom.common_name}</div>
            <div className="text-xs text-gray-300">{mushroom.scientific_name}</div>
            <div className="text-xs text-gray-400">{mushroom.total_studies} studies</div>
          </div>
        </Html>
      )}
      {/* Subtle glow effect */}
      <Sphere args={[size * 1.05, 32, 32]}>
        <meshBasicMaterial
          color="#C8A53D"
          transparent
          opacity={0.15}
        />
      </Sphere>
    </group>
  );
}

// Compound Node Component (Green Spheres)
function CompoundNode({ compound, position, onClick, onHover }: CompoundNodeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame(() => {
    if (meshRef.current && hovered) {
      meshRef.current.scale.lerp(new THREE.Vector3(1.3, 1.3, 1.3), 0.1);
    } else if (meshRef.current) {
      meshRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
    }
  });

  return (
    <group position={position}>
      <Sphere
        ref={meshRef}
        args={[0.6, 24, 24]}
        onClick={onClick}
        onPointerOver={() => {
          setHovered(true);
          onHover(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          onHover(false);
          document.body.style.cursor = 'auto';
        }}
      >
        <meshStandardMaterial
          color="#C26A1E"
          emissive="#C26A1E"
          emissiveIntensity={hovered ? 0.4 : 0.15}
          roughness={0.3}
          metalness={0.7}
        />
      </Sphere>
      {hovered && (
        <Html distanceFactor={10}>
          <div className="bg-black/80 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap max-w-xs">
            <div className="font-bold truncate">{compound.name}</div>
            <div className="text-xs text-gray-300">{compound.category}</div>
            {compound.chemical_name && (
              <div className="text-xs text-gray-400 truncate">{compound.chemical_name}</div>
            )}
          </div>
        </Html>
      )}
    </group>
  );
}

// Condition Node Component (Purple Spheres)
function ConditionNode({ condition, position, onClick, onHover }: ConditionNodeProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const [hovered, setHovered] = useState(false);

  useFrame(() => {
    if (meshRef.current && hovered) {
      meshRef.current.scale.lerp(new THREE.Vector3(1.25, 1.25, 1.25), 0.1);
    } else if (meshRef.current) {
      meshRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
    }
  });

  return (
    <group position={position}>
      <Sphere
        ref={meshRef}
        args={[0.7, 24, 24]}
        onClick={onClick}
        onPointerOver={() => {
          setHovered(true);
          onHover(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          onHover(false);
          document.body.style.cursor = 'auto';
        }}
      >
        <meshStandardMaterial
          color="#B45F3D"
          emissive="#B45F3D"
          emissiveIntensity={hovered ? 0.5 : 0.2}
          roughness={0.3}
          metalness={0.6}
        />
      </Sphere>
      {hovered && (
        <Html distanceFactor={10}>
          <div className="bg-black/80 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap">
            <div className="font-bold">{condition.name}</div>
            <div className="text-xs text-gray-300">{condition.category}</div>
          </div>
        </Html>
      )}
    </group>
  );
}

// Study Particle Component (Blue Traveling Dots)
function StudyParticle({ study, start, end, speed }: StudyParticleProps) {
  const meshRef = useRef<THREE.Mesh>(null);
  const progress = useRef(Math.random());

  useFrame((state, delta) => {
    if (meshRef.current) {
      progress.current += speed * delta;
      if (progress.current > 1) progress.current = 0;

      const t = progress.current;
      const x = start[0] + (end[0] - start[0]) * t;
      const y = start[1] + (end[1] - start[1]) * t;
      const z = start[2] + (end[2] - start[2]) * t;

      meshRef.current.position.set(x, y, z);
    }
  });

  return (
    <Trail
      width={2}
      length={6}
      color="#A98A2C"
      attenuation={(t) => t * t}
    >
      <mesh ref={meshRef}>
        <sphereGeometry args={[0.15, 8, 8]} />
        <meshBasicMaterial
          color="#A98A2C"
          transparent
          opacity={0.8}
        />
      </mesh>
    </Trail>
  );
}

// Connection Lines Component
function ConnectionLines({
  mushroomPositions,
  compoundPositions,
  conditionPositions,
  database
}: {
  mushroomPositions: Map<string, [number, number, number]>;
  compoundPositions: Map<string, [number, number, number]>;
  conditionPositions: Map<string, [number, number, number]>;
  database: DatabaseState;
}) {
  const lines = useMemo(() => {
    const lineData: Array<{
      start: [number, number, number],
      end: [number, number, number],
      color: string,
      opacity: number
    }> = [];

    // Lines from mushrooms to compounds
    database.study_compounds?.forEach(sc => {
      const intervention = database.interventions?.find(i => i.study_id === sc.study_id);
      if (intervention) {
        const mushroomPos = mushroomPositions.get(intervention.mushroom_id);
        const compoundPos = compoundPositions.get(sc.compound_id);
        if (mushroomPos && compoundPos) {
          lineData.push({
            start: mushroomPos,
            end: compoundPos,
            color: '#C26A1E',
            opacity: 0.15
          });
        }
      }
    });

    // Lines from mushrooms to conditions (via studies)
    database.study_conditions?.forEach(sc => {
      const intervention = database.interventions?.find(i => i.study_id === sc.study_id);
      if (intervention) {
        const mushroomPos = mushroomPositions.get(intervention.mushroom_id);
        const conditionPos = conditionPositions.get(sc.condition_id);
        if (mushroomPos && conditionPos) {
          lineData.push({
            start: mushroomPos,
            end: conditionPos,
            color: '#ffffff',
            opacity: 0.08
          });
        }
      }
    });

    return lineData;
  }, [database, mushroomPositions, compoundPositions, conditionPositions]);

  return (
    <>
      {lines.map((line, i) => {
        const points = [
          new THREE.Vector3(...line.start),
          new THREE.Vector3(...line.end)
        ];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);

        return (
          <line key={i} geometry={geometry}>
            <lineBasicMaterial
              color={line.color}
              transparent
              opacity={line.opacity}
            />
          </line>
        );
      })}
    </>
  );
}

// Main Scene Component
function Scene({ database, autoRotate }: KnowledgeGraph3DProps) {
  const [selectedNode, setSelectedNode] = useState<any>(null);

  // Calculate positions for mushroom nodes in a spiral
  const mushroomPositions = useMemo(() => {
    const positions = new Map<string, [number, number, number]>();
    const mushrooms = database.mushrooms || [];
    const radius = 15;
    const heightSpread = 10;

    mushrooms.forEach((mushroom, i) => {
      const angle = (i / mushrooms.length) * Math.PI * 2;
      const spiralRadius = radius + (i / mushrooms.length) * 5;
      const x = Math.cos(angle) * spiralRadius;
      const y = ((i / mushrooms.length) - 0.5) * heightSpread;
      const z = Math.sin(angle) * spiralRadius;
      positions.set(mushroom.mushroom_id, [x, y, z]);
    });

    return positions;
  }, [database.mushrooms]);

  // Calculate positions for compound nodes (grouped by category, near related mushrooms)
  const compoundPositions = useMemo(() => {
    const positions = new Map<string, [number, number, number]>();
    const compounds = database.compounds || [];

    // Group compounds by category
    const compoundsByCategory = new Map<string, Compound[]>();
    compounds.forEach(compound => {
      const category = compound.category || 'Other';
      if (!compoundsByCategory.has(category)) {
        compoundsByCategory.set(category, []);
      }
      compoundsByCategory.get(category)!.push(compound);
    });

    // Position compounds near their related mushrooms
    const compoundMushrooms = new Map<string, string[]>();
    database.study_compounds?.forEach(sc => {
      const intervention = database.interventions?.find(i => i.study_id === sc.study_id);
      if (intervention) {
        if (!compoundMushrooms.has(sc.compound_id)) {
          compoundMushrooms.set(sc.compound_id, []);
        }
        if (!compoundMushrooms.get(sc.compound_id)!.includes(intervention.mushroom_id)) {
          compoundMushrooms.get(sc.compound_id)!.push(intervention.mushroom_id);
        }
      }
    });

    compounds.forEach((compound, i) => {
      // Find related mushrooms
      const relatedMushrooms = compoundMushrooms.get(compound.compound_id) || [];

      if (relatedMushrooms.length > 0) {
        // Position near the first related mushroom
        const mushroomPos = mushroomPositions.get(relatedMushrooms[0]);
        if (mushroomPos) {
          // Offset from mushroom position
          const angle = (i / compounds.length) * Math.PI * 2;
          const offset = 4; // Distance from mushroom
          const x = mushroomPos[0] + Math.cos(angle) * offset;
          const y = mushroomPos[1] + (Math.random() - 0.5) * 2;
          const z = mushroomPos[2] + Math.sin(angle) * offset;
          positions.set(compound.compound_id, [x, y, z]);
          return;
        }
      }

      // Fallback: position in middle layer if no mushroom relationship found
      const angle = (i / compounds.length) * Math.PI * 2;
      const radius = 22;
      const x = Math.cos(angle) * radius;
      const y = ((i / compounds.length) - 0.5) * 8;
      const z = Math.sin(angle) * radius;
      positions.set(compound.compound_id, [x, y, z]);
    });

    return positions;
  }, [database.compounds, database.study_compounds, database.interventions, mushroomPositions]);

  // Calculate positions for condition nodes in outer sphere
  const conditionPositions = useMemo(() => {
    const positions = new Map<string, [number, number, number]>();
    const conditions = database.conditions || [];
    const radius = 30;

    conditions.forEach((condition, i) => {
      // Fibonacci sphere algorithm for even distribution
      const phi = Math.acos(-1 + (2 * i) / conditions.length);
      const theta = Math.sqrt(conditions.length * Math.PI) * phi;

      const x = radius * Math.cos(theta) * Math.sin(phi);
      const y = radius * Math.sin(theta) * Math.sin(phi);
      const z = radius * Math.cos(phi);

      positions.set(condition.condition_id, [x, y, z]);
    });

    return positions;
  }, [database.conditions]);

  // Create study particles
  const studyParticles = useMemo(() => {
    const particles: Array<{
      study: Study;
      start: [number, number, number];
      end: [number, number, number];
    }> = [];

    database.study_conditions?.slice(0, 40).forEach(sc => { // Show more particles
      const intervention = database.interventions?.find(i => i.study_id === sc.study_id);
      const study = database.studies?.find(s => s.study_id === sc.study_id);

      if (intervention && study) {
        const start = mushroomPositions.get(intervention.mushroom_id);
        const end = conditionPositions.get(sc.condition_id);

        if (start && end) {
          particles.push({ study, start, end });
        }
      }
    });

    return particles;
  }, [database, mushroomPositions, conditionPositions]);

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <pointLight position={[20, 20, 20]} intensity={1.2} />
      <pointLight position={[-20, -10, -20]} intensity={0.6} />
      <pointLight position={[0, 30, 0]} intensity={0.8} color="#B45F3D" />

      {/* Mushroom Nodes */}
      {Array.from(mushroomPositions.entries()).map(([id, position]) => {
        const mushroom = database.mushrooms?.find(m => m.mushroom_id === id);
        if (!mushroom) return null;

        return (
          <MushroomNode
            key={id}
            mushroom={mushroom}
            position={position}
            onClick={() => setSelectedNode({ type: 'mushroom', data: mushroom })}
            onHover={() => {}}
          />
        );
      })}

      {/* Compound Nodes - Show ALL compounds */}
      {Array.from(compoundPositions.entries()).map(([id, position]) => {
        const compound = database.compounds?.find(c => c.compound_id === id);
        if (!compound) return null;

        return (
          <CompoundNode
            key={id}
            compound={compound}
            position={position}
            onClick={() => setSelectedNode({ type: 'compound', data: compound })}
            onHover={() => {}}
          />
        );
      })}

      {/* Condition Nodes */}
      {Array.from(conditionPositions.entries()).map(([id, position]) => {
        const condition = database.conditions?.find(c => c.condition_id === id);
        if (!condition) return null;

        return (
          <ConditionNode
            key={id}
            condition={condition}
            position={position}
            onClick={() => setSelectedNode({ type: 'condition', data: condition })}
            onHover={() => {}}
          />
        );
      })}

      {/* Study Particles */}
      {studyParticles.map((particle, i) => (
        <StudyParticle
          key={i}
          study={particle.study}
          start={particle.start}
          end={particle.end}
          speed={0.2 + Math.random() * 0.3}
        />
      ))}

      {/* Connection Lines */}
      <ConnectionLines
        mushroomPositions={mushroomPositions}
        compoundPositions={compoundPositions}
        conditionPositions={conditionPositions}
        database={database}
      />

      {/* Camera Controls */}
      <OrbitControls
        autoRotate={autoRotate}
        autoRotateSpeed={0.5}
        enableDamping
        dampingFactor={0.05}
        minDistance={10}
        maxDistance={80}
      />
    </>
  );
}

// Main Component
export function KnowledgeGraph3D({ database, autoRotate = true }: KnowledgeGraph3DProps) {
  // Validate database structure
  if (!database || !database.mushrooms || !database.compounds || !database.conditions) {
    console.error('[KnowledgeGraph3D] Invalid database structure:', database);
    return (
      <div style={{ width: '100%', height: '600px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#ef4444', textAlign: 'center', padding: '2rem' }}>
          <p>Unable to render 3D graph: Missing required data</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#9ca3af' }}>
            Please check browser console for details
          </p>
        </div>
      </div>
    );
  }

  console.log('[KnowledgeGraph3D] Rendering integrated network with:', {
    mushrooms: database.mushrooms?.length,
    compounds: database.compounds?.length,
    conditions: database.conditions?.length,
    studies: database.studies?.length
  });

  return (
    <div style={{ width: '100%', height: '75vh', minHeight: '600px' }}>
      <Canvas
        camera={{ position: [30, 20, 30], fov: 75 }}
        gl={{ antialias: true, alpha: true, preserveDrawingBuffer: true }}
        onCreated={(state) => {
          console.log('[KnowledgeGraph3D] Canvas created successfully');
        }}
        onError={(error) => {
          console.error('[KnowledgeGraph3D] Canvas error:', error);
        }}
      >
        <Suspense fallback={null}>
          <Scene database={database} autoRotate={autoRotate} />
        </Suspense>
      </Canvas>
    </div>
  );
}
